import { Link } from 'react-router-dom'
import { useCurrencyListScreen } from './hooks/useCurrencyListScreen'
import { appToast } from '../../../components/AppToast'
import { useEffect } from 'react'

function statusChipClasses(isActive) {
  return isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-surface-variant text-on-surface-variant'
}

function statusDotClasses(isActive) {
  return isActive ? 'bg-emerald-500' : 'bg-outline'
}

export default function CurrencyMasterListScreen() {
  const { state, actions } = useCurrencyListScreen()

  useEffect(() => {
    if (state.error) appToast.error(state.error)
  }, [state.error])

  useEffect(() => {
    if (state.success) appToast.success(state.success)
  }, [state.success])

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-7 pb-8">
      <section className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.18em] text-primary">Financial Configuration</p>
          <h1 className="mt-2 text-4xl font-extrabold leading-[0.98] tracking-[-0.02em] text-on-surface">
            Currency Master
          </h1>
        </div>
        <p className="max-w-xl text-right text-base leading-relaxed text-on-surface-variant">
          Configure and manage ISO currencies used across your organization. Set exchange rates for
          multi-currency financial reporting and project billing.
        </p>
      </section>

      <section className="grid grid-cols-1 gap-4 xl:grid-cols-[1fr_280px]">
        <article className="rounded-[2rem] border border-outline-variant/35 bg-surface-container-low p-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:items-end">
            <label className="grid gap-2">
              <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">
                Currency Code / Name
              </span>
              <input
                type="text"
                value={state.searchText}
                onChange={(e) => actions.setSearchText(e.target.value)}
                placeholder="Filter by code or name..."
                className="w-full rounded-xl border-0 bg-surface-container-lowest px-4 py-3 text-sm text-on-surface outline-none placeholder:text-on-surface-variant"
              />
            </label>

            <div className="grid gap-2">
              <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">Status</span>
              <div className="flex gap-2 rounded-xl bg-surface-container-high p-1">
                <button
                  type="button"
                  onClick={() => actions.setStatusFilter('active')}
                  className={`flex-1 rounded-lg px-4 py-2.5 text-sm font-bold transition-colors ${
                    state.statusFilter === 'active'
                      ? 'bg-primary text-on-primary shadow-sm'
                      : 'bg-surface-container-lowest text-on-surface-variant hover:text-on-surface'
                  }`}
                >
                  Active
                </button>
                <button
                  type="button"
                  onClick={() => actions.setStatusFilter('inactive')}
                  className={`flex-1 rounded-lg px-4 py-2.5 text-sm font-bold transition-colors ${
                    state.statusFilter === 'inactive'
                      ? 'bg-primary text-on-primary shadow-sm'
                      : 'bg-surface-container-lowest text-on-surface-variant hover:text-on-surface'
                  }`}
                >
                  Inactive
                </button>
              </div>
            </div>
          </div>
        </article>

        <article className="rounded-[2rem] bg-primary p-6 text-on-primary shadow-lg shadow-primary/20">
          <div className="flex items-start justify-between">
            <span className="material-symbols-outlined text-3xl">currency_exchange</span>
            <span className="text-4xl font-black">{state.activeCount}</span>
          </div>
          <p className="mt-4 text-lg font-semibold leading-tight text-white/75">
            Active currencies across the organization
          </p>
        </article>
      </section>

      <section className="overflow-hidden rounded-[2rem] border border-outline-variant/35 bg-surface-container-lowest">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-left">
            <thead>
              <tr className="bg-surface-container-low/60 text-[11px] uppercase tracking-[0.14em] text-on-surface-variant">
                <th className="px-8 py-5 font-black">Currency</th>
                <th className="px-8 py-5 font-black">Symbol</th>
                <th className="px-8 py-5 font-black">Decimals</th>
                <th className="px-8 py-5 font-black">Rounding</th>
                <th className="px-8 py-5 font-black text-right">Exchange Rate</th>
                <th className="px-8 py-5 font-black">Status</th>
                <th className="px-8 py-5 text-right font-black">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-container-low">
              {state.isLoading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-sm text-on-surface-variant">
                    Loading currencies...
                  </td>
                </tr>
              ) : state.rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-sm text-on-surface-variant">
                    No currencies found for selected filters.
                  </td>
                </tr>
              ) : (
                state.rows.map((row) => {
                  const code = row.currencyCode || row.currency_code
                  const name = row.currencyName || row.currency_name
                  const symbol = row.symbol
                  const decimals = row.decimalPlaces ?? row.decimal_places ?? 2
                  const rounding = row.roundingMethod || row.rounding_method || 'ROUND_OFF'
                  const rate = row.exchangeRate ?? row.exchange_rate ?? 1
                  const isBase = row.isBase === 1 || row.is_base === 1
                  const isActive = row.isActive === 1 || row.is_active === 1

                  return (
                    <tr key={code} className="transition-colors hover:bg-surface-container-low/30">
                      <td className="px-8 py-6">
                        <div className="flex items-center gap-4">
                          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-secondary-container text-sm font-black text-primary">
                            {symbol || code?.slice(0, 2)}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="text-lg font-bold leading-tight text-on-surface">{code}</p>
                              {isBase && (
                                <span className="rounded-full bg-primary px-2 py-0.5 text-[9px] font-black uppercase tracking-wider text-on-primary">
                                  Base
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-on-surface-variant">{name}</p>
                          </div>
                        </div>
                      </td>

                      <td className="px-8 py-6 font-mono text-xl font-bold text-on-surface">{symbol}</td>

                      <td className="px-8 py-6 font-mono text-base text-on-surface">{decimals}</td>

                      <td className="px-8 py-6 text-sm text-on-surface-variant">{rounding.replace('_', ' ')}</td>

                      <td className="px-8 py-6 text-right font-mono font-bold text-on-surface">
                        {isBase ? '1.000000' : Number(rate).toFixed(6)}
                      </td>

                      <td className="px-8 py-6">
                        <span
                          className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-bold ${statusChipClasses(isActive)}`}
                        >
                          <span className={`h-1.5 w-1.5 rounded-full ${statusDotClasses(isActive)}`} />
                          {isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>

                      <td className="px-8 py-6">
                        <div className="flex items-center justify-end gap-2">
                          <Link
                            to={`/masters/currency/entry?code=${code}`}
                            className="rounded-full p-2 text-outline transition-colors hover:bg-surface-container-high hover:text-primary"
                            title="Edit currency"
                          >
                            <span className="material-symbols-outlined">edit</span>
                          </Link>
                          {!isBase && (
                            <button
                              type="button"
                              disabled={state.isMutating}
                              onClick={() => actions.handleDelete(code)}
                              className="rounded-full p-2 text-outline transition-colors hover:bg-error-container hover:text-error disabled:cursor-not-allowed disabled:opacity-60"
                              title="Delete currency"
                            >
                              <span className="material-symbols-outlined">delete</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="border-t border-outline-variant/30 bg-surface-container-low/30 px-8 py-4">
          <span className="text-sm text-on-surface-variant">
            Showing {state.rows.length} of {state.totalCount} currencies
          </span>
        </div>
      </section>
    </div>
  )
}
